import { createSelector } from '@reduxjs/toolkit';
import { cloneDeep, difference, union } from 'es-toolkit';
import ms from 'ms';

import api from '@/redux/api/sellerCatalogs';
import { getAuthToken } from '@/redux/modules/account/user/user.selectors';
import { getDeployment } from '@/redux/modules/config';
import { AppDispatch, AppGetState, GlobalState } from '@/redux/store';

export const LOAD_SELLER_PREVIEW_CATALOGS_FAIL = 'la/domain/sellerPreviewCatalogs/LOAD_FAIL';
export const LOAD_SELLER_PREVIEW_CATALOGS_REQUEST = 'la/domain/sellerPreviewCatalogs/LOAD_REQUEST';
export const LOAD_SELLER_PREVIEW_CATALOGS_SUCCESS = 'la/domain/sellerPreviewCatalogs/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');

export type SellerPreviewCatalogsSlice = {
    byCatalogId: any;
    byId: any;
    loaded: any;
    loading: any[];
};

export const defaultSellerPreviewCatalogsSlice: SellerPreviewCatalogsSlice = {
    byCatalogId: {},
    byId: {},
    loaded: {},
    loading: [],
};

export default function reducer(
    state: SellerPreviewCatalogsSlice = defaultSellerPreviewCatalogsSlice,
    action: any = {}
): SellerPreviewCatalogsSlice {
    switch (action.type) {
        case LOAD_SELLER_PREVIEW_CATALOGS_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.sellerId]),
            };
        case LOAD_SELLER_PREVIEW_CATALOGS_REQUEST:
            return {
                ...state,
                loading: union(state.loading, [action.payload]),
            };
        case LOAD_SELLER_PREVIEW_CATALOGS_SUCCESS:
            const { sellerId } = action.meta;
            const existing = cloneDeep(state.byCatalogId);
            action.payload.catalogPreviews.forEach((preview) => {
                existing[preview.previewId] = preview;
            });
            return {
                ...state,
                byCatalogId: existing,
                byId: {
                    ...state.byId,
                    [sellerId]: [...action.payload.catalogPreviews].sort((a, b) => {
                        return a.saleStartTs - b.saleStartTs;
                    }),
                },
                loaded: { ...state.loaded, [sellerId]: action.meta.actionTime },
                loading: difference(state.loading, [sellerId]),
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state;
const sellerPreviewCatalogsSlice = createSelector(stateSelector, (state) => state.sellerPreviewCatalogs);

const idSelector = (_: GlobalState, id: number) => id;

const byIdSelector = createSelector(sellerPreviewCatalogsSlice, (state) => state.byId);
const byCatalogIdSelector = createSelector(sellerPreviewCatalogsSlice, (state) => state.byCatalogId);

const loadedSelector = createSelector(sellerPreviewCatalogsSlice, (state) => state.loaded);

const loadingSelector = createSelector(sellerPreviewCatalogsSlice, (state) => state.loading);

// takes in a (state, id) and returns the data
export const getSellerPreviewCatalogs = createSelector([byIdSelector, idSelector], (byId, id) => byId[id] || []);
export const getSellerPreviewCatalogIds = createSelector(
    getSellerPreviewCatalogs,
    (previewCatalogs) => (previewCatalogs || []).map((prev) => prev.previewId) || []
);

export const getCatalogPreview = createSelector(
    [byCatalogIdSelector, idSelector],
    (byCatalogId, id) => byCatalogId[id] || {}
);

// takes in a (state, id) and returns the load time
export const getLoadTimeForSellerPreviewCatalogs = createSelector(
    [loadedSelector, idSelector],
    (loaded, id) => loaded[id] || 0
);

// takes in a (state, id) and returns the is loading
export const isSellerPreviewCatalogsLoading = createSelector([loadingSelector, idSelector], (loading, id) => {
    return loading.includes(id);
});

const shouldFetchPreviewCatalogs = (state, sellerId) => {
    if (!sellerId) {
        return false;
    }
    const item = getSellerPreviewCatalogs(state, sellerId);
    if (item.length) {
        const loaded = getLoadTimeForSellerPreviewCatalogs(state, sellerId);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) {
            return false;
        }
    }
    const loading = isSellerPreviewCatalogsLoading(state, sellerId);
    return !loading;
};

/* ACTION CREATORS */
const loadPreviewCatalogs = (sellerId) => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch({
            payload: sellerId,
            type: LOAD_SELLER_PREVIEW_CATALOGS_REQUEST,
        });
        const response = await api.fetchSellerPreviewCatalogsById({
            authToken,
            deployment,
            sellerId,
        });
        return dispatch({
            meta: { actionTime: Date.now(), sellerId },
            payload: response.data,
            type: LOAD_SELLER_PREVIEW_CATALOGS_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            meta: { sellerId },
            payload: error,
            type: LOAD_SELLER_PREVIEW_CATALOGS_FAIL,
        });
    }
};

export const fetchSellerPreviewCatalogsIfNeeded =
    (sellerId: number) => async (dispatch: AppDispatch, getState: AppGetState) => {
        if (shouldFetchPreviewCatalogs(getState(), sellerId)) {
            return dispatch(loadPreviewCatalogs(sellerId));
        }
        return Promise.resolve();
    };
